import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Flame } from 'lucide-react';
import Button from './Button';
import Card from './Card';

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const {
    id,
    name,
    slug,
    image,
    weight,
    price,
    offer_price,
    current_price,
    is_on_sale,
    stock,
    featured,
  } = product;

  const isOutOfStock = stock <= 0;
  // Usar current_price del backend si está disponible
  const hasOffer = is_on_sale || (offer_price && offer_price < price);
  const finalPrice = current_price || (hasOffer && offer_price ? offer_price : price);
  const discount = hasOffer ? Math.round((1 - offer_price / price) * 100) : 0;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!isOutOfStock && onAddToCart) {
      onAddToCart(product);
      navigate('/carrito');
    }
  };

  return (
    <Card 
      hover={!isOutOfStock}
      className={`h-full flex flex-col ${isOutOfStock ? 'opacity-75' : ''}`}
    >
      {/* Image Container */}
      <div className="relative mb-4">
        <div className="aspect-square rounded-xl overflow-hidden bg-gray-50">
          {image ? (
            <img
              src={image}
              alt={name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <Flame size={48} />
            </div>
          )}
        </div>

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {featured && (
            <span className="px-2 py-1 bg-primary text-white text-xs font-bold rounded-lg">
              Destacado
            </span>
          )}
          {hasOffer && (
            <span className="px-2 py-1 bg-success text-white text-xs font-bold rounded-lg">
              -{discount}%
            </span>
          )}
        </div>

        {/* Out of Stock Badge */}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
            <span className="px-3 py-1.5 bg-secondary text-white text-sm font-bold rounded-lg">
              Agotado
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        <h3 className="font-display font-bold text-text text-lg mb-1 line-clamp-2">
          {name}
        </h3>
        
        {weight && (
          <p className="text-sm text-gray-500 mb-3">
            {weight}
          </p>
        )}

        {/* Price */}
        <div className="mt-auto">
          <div className="flex items-baseline gap-2 mb-3">
            {hasOffer && (
              <span className="text-sm text-gray-400 line-through">
                ${price.toLocaleString()}
              </span>
            )}
            <span className={`font-display font-bold text-xl ${hasOffer ? 'text-success' : 'text-primary'}`}>
              ${finalPrice.toLocaleString()}
            </span>
          </div>

          <Button
            variant={isOutOfStock ? 'ghost' : 'primary'}
            size="md"
            fullWidth
            disabled={isOutOfStock}
            onClick={handleAddToCart}
            className="gap-2"
          >
            <ShoppingCart size={18} />
            {isOutOfStock ? 'Agotado' : 'Agregar'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ProductCard;
